import { DatePipe, NgClass, NgStyle, SlicePipe } from '@angular/common';
import { Component, DestroyRef, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BookingService } from '@app/services/feature/booking.service';
import { TimezoneService } from '@app/services/feature/timezone.service';
import { WIDGET_DISPLAY_TIMEZONE_SOURCE } from '@constants/app.constants';
import { DATE_ISO_FORMAT, DATE_TIME_ISO_T_FORMAT } from '@constants/date.constants';
import { environment } from '@environments/environment';
import { AppointmentCartItem } from '@models/selected-booking-data.model';
import { TimezoneDbModel } from '@models/timezone.model';
import { TranslateModule } from '@ngx-translate/core';
import { CustomEventService } from '@services/feature/custom-event.service';
import { DateUtilService } from '@services/utils/date-util.service';
import { LoggerService } from '@services/utils/logger.service';
import { LoaderComponent } from '@ui-lib/loader/loader.component';
import { TimezoneSelectComponent } from '@ui-lib/timezone-select/timezone-select.component';
import { CalendarCommonModule, CalendarMonthModule, CalendarMonthViewDay, CalendarUtils } from 'angular-calendar';
import { WeekDay } from 'calendar-utils';
import { addDays, addMonths, addWeeks, subMonths } from 'date-fns';
import moment from 'moment';
import { Subject } from 'rxjs';

type CalendarPeriod = 'day' | 'week' | 'month';

function addPeriod(period: CalendarPeriod, date: Date, amount: number): Date {
  return {
    day: addDays,
    week: addWeeks,
    month: addMonths,
  }[period](date, amount);
}

@Component({
  selector: 'app-calio-calendar-picker',
  templateUrl: './calio-calendar-picker.component.html',
  styleUrls: ['./calio-calendar-picker.component.scss'],
  imports: [NgStyle, LoaderComponent, NgClass, CalendarCommonModule, CalendarMonthModule, SlicePipe, DatePipe, TranslateModule, TimezoneSelectComponent]
})
export class CalioCalendarPickerComponent implements OnInit {

  private utils = inject(CalendarUtils);
  private customEventService = inject(CustomEventService);
  protected bookingService = inject(BookingService);
  protected readonly widgetDisplayTimezoneSource = WIDGET_DISPLAY_TIMEZONE_SOURCE;
  private timezoneService = inject(TimezoneService);
  private dateUtilService = inject(DateUtilService);
  private destroyRef = inject(DestroyRef);

  readonly dateIsoFormat = DATE_ISO_FORMAT;
  readonly dateTimeIsoTFormat = DATE_TIME_ISO_T_FORMAT;

  @Input() displayBadgeInBookingFeature = false;
  @Input() disableAllDays = false;
  @Input() selectedDate: Date;
  @Input() inactiveWeekDays: number[] = [];
  @Input() availableDays: string[] = [];
  @Input() isMultiselect = true;
  @Input() futureBookingThreshold = 0;
  @Input() disableAutomaticSearch = 1;
  @Input() timezones: TimezoneDbModel[] = [];
  @Input() selectedBookingCartItems: AppointmentCartItem[] = [];

  @Output() selectDaysEvent: EventEmitter<{
    day: WeekDay[],
    isUserInteracted: boolean
  }> = new EventEmitter();
  @Output() monthChangedEvent = new EventEmitter<Date>();
  @Output('onTimezoneChange') onTimezoneChangeEvent = new EventEmitter<string>();

  view: CalendarPeriod = 'month';
  columnHeaders: WeekDay[];
  minDate = subMonths(new Date(), 0);
  maxDate = addMonths(new Date(), 12);
  prevBtnDisabled = false;
  nextBtnDisabled = false;
  selectedMonthViewDay: WeekDay;
  selectedDayViewDate: Date;
  selectedDays: WeekDay[] = [];
  imagePath: string = `${environment.deployUrl}assets/images/icons`;
  isCalendarLoading = true;
  viewDate: Date = new Date();
  lastCalendarState: string;
  updateCalendarState = new Subject<void>();

  constructor() {
    const today: Date = new Date();
    const yesterday: Date = new Date(today);
    yesterday.setDate(today.getDate() - 1);

    this.minDate = subMonths(yesterday, 0);
  }

  ngOnInit(): void {
    const timezone = this.bookingService.selectedBookingData?.timezone || this.timezoneService.localTimezone || 'Europe/Zurich';
    const timeZoneWiseLocalDate = new Date(moment.tz(moment(), timezone).format(DATE_TIME_ISO_T_FORMAT));

    this.viewDate = timeZoneWiseLocalDate;
    this.viewDate.setHours(0, 0, 0, 0);

    this.minDate = timeZoneWiseLocalDate;
    this.minDate.setHours(0, 0, 0, 0);

    this.customEventService.reloadCalendarEvent.subscribe(() => {
      this.disableAllDays = true;
      this.isCalendarLoading = true;
      this.monthChangedEvent.emit(this.viewDate);
    });

    this.customEventService.refreshCalendar.subscribe(() => {
      setTimeout(() => {
        const timezone = this.bookingService.selectedBookingData?.timezone || this.timezoneService.localTimezone || 'Europe/Zurich';
        const timeZoneWiseLocalDate = new Date(moment.tz(moment(), timezone).format(DATE_TIME_ISO_T_FORMAT));

        this.minDate = subMonths(timeZoneWiseLocalDate, 0);

        if (this.futureBookingThreshold > 0) {
          this.maxDate = addDays(timeZoneWiseLocalDate, this.futureBookingThreshold);
        } else {
          this.maxDate = addMonths(timeZoneWiseLocalDate, 12);
        }

        this.minDate.setHours(0, 0, 0, 0);
        this.maxDate.setHours(23, 59, 59, 999);

        this.disableAllDays = false;
        this.isCalendarLoading = false;
        this.selectedDays = [];
        // this.selectedBookingCartItems = [];

        this.updateCalendarState.next();
      }, 500);
    });

    this.customEventService.resetRecurringDatesEvent.subscribe(
      () => {
        for (const day of this.selectedDays) {
          this.onSelectDay(day, false);
        }
      }
    );

    this.customEventService.refreshCalendarBadgeInBookingEvent
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result: { cartItems: AppointmentCartItem[] }) => {
        setTimeout(() => {
          // this.disableAllDays = false;
          this.updateCalendarState.next();
        }, 500);
      }
      );

    // futureBookingThreshold
    if (this.futureBookingThreshold > 0) {
      this.maxDate = addDays(timeZoneWiseLocalDate, this.futureBookingThreshold);
      this.maxDate.setHours(23, 59, 59, 999);
    }

    this.refreshHeader();
    this.disableButtons();
  }

  increment(): void {
    this.lastCalendarState = 'next';
    const newDate = addMonths(this.viewDate, 1);
    newDate.setDate(1);
    newDate.setHours(0, 0, 0, 0);
    this.changeDate(newDate);
  }

  decrement(): void {
    this.lastCalendarState = 'previous';
    this.changeDate(subMonths(this.viewDate, 1));
  }

  today(): void {
    this.changeDate(new Date());
  }

  dateIsValid(date: Date): boolean {
    return date >= this.minDate && date <= this.maxDate;
  }

  changeDate(date: Date): void {
    date.setHours(0, 0, 0, 0);
    this.viewDate = date;
    this.dateOrViewChanged();
  }

  changeView(view: CalendarPeriod): void {
    this.viewDate.setHours(0, 0, 0, 0);
    this.view = view;
    this.dateOrViewChanged();
  }

  dateOrViewChanged(): void {
    this.isCalendarLoading = true;
    this.disableButtons();
    if (this.viewDate < this.minDate) {
      LoggerService.warn('View date is less than min date ');
      if (this.viewDate.getMonth() === this.minDate.getMonth() && this.viewDate.getFullYear() === this.minDate.getFullYear()) {
        LoggerService.log('Month and Year of mindate and view date is same thats why loading the calendar data ');
        this.disableAllDays = true;
        this.monthChangedEvent.emit(this.viewDate);
      }
    } else if (this.viewDate > this.maxDate) {
      LoggerService.warn('View date is greater than max date ');
      this.isCalendarLoading = false;
    } else {
      LoggerService.log('month change event called');
      this.disableAllDays = true;
      this.monthChangedEvent.emit(this.viewDate);
    }
  }

  disableButtons(): void {
    if (this.viewDate.getFullYear() === this.maxDate.getFullYear()) {
      if (this.viewDate.getMonth() >= this.maxDate.getMonth()) {
        this.nextBtnDisabled = true;
      } else {
        this.nextBtnDisabled = false;
      }
    } else if (this.viewDate.getFullYear() < this.maxDate.getFullYear()) {
      this.nextBtnDisabled = false;
    } else {
      this.nextBtnDisabled = true;
    }

    if (this.viewDate.getMonth() <= this.minDate.getMonth() && this.viewDate.getFullYear() <= this.minDate.getFullYear()) {
      this.prevBtnDisabled = true;
    } else {
      this.prevBtnDisabled = false;
    }

  }

  beforeMonthViewRender({ body }: { body: CalendarMonthViewDay[] }): void {
    const modifiedInactiveDays: number[] = [];
    for (const day of this.inactiveWeekDays) {
      if (day === 7) {
        modifiedInactiveDays.push(0);
      } else {
        modifiedInactiveDays.push(day);
      }
    }
    body.forEach(day => {
      if (this.disableAllDays) {
        day.cssClass = 'cal-disabled';
        return true;
      }

      if (
        !this.dateIsValid(day.date) ||
        !day.inMonth ||
        !this.availableDays.includes(this.dateUtilService.getFormattedDateByFormat(day.date, this.dateIsoFormat)) ||
        modifiedInactiveDays?.indexOf(day.day) > -1
      ) {
        day.cssClass = 'cal-disabled';
      }

      if (
        this.selectedDays.some(
          selectedDay => selectedDay.date.getTime() === day.date.getTime()
        )
      ) {
        day.cssClass = 'cal-selected';
        this.setBadgeCount(day, 'cal-selected');
      } else if (
        this.selectedDate &&
        (this.dateUtilService.getFormattedDateByFormat(this.selectedDate, this.dateIsoFormat)
          ===
          this.dateUtilService.getFormattedDateByFormat(day.date, this.dateIsoFormat))
      ) {
        // day.cssClass = 'cal-selected';
        this.onSelectDay(day, false);
      } else {
        this.setBadgeCount(day, 'cal-selected-without-highlight');
      }
    });
    if (!this.disableAllDays && this.lastCalendarState !== 'previous') {
      this.autoSearch({ body });
    }
  }

  autoSearch({ body }: { body: CalendarMonthViewDay[] }): void {
    LoggerService.log('body is ', body);
    let isAllDaysAreDisabled: boolean = Boolean(true);
    for (const day of body) {
      if (day.cssClass !== 'cal-disabled') {
        isAllDaysAreDisabled = false;
        break;
      }
    }
    if (isAllDaysAreDisabled) {
      LoggerService.log('disableAutomaticSearch ', this.disableAutomaticSearch);
      if (this.disableAutomaticSearch === 0) {

        LoggerService.log('Inside automatic search block this.viewDate ', this.viewDate, this.maxDate);
        const date: Date = addPeriod(this.view, this.viewDate, 1);

        LoggerService.log('date.getMonth() ', date.getMonth());
        LoggerService.log('this.maxDate.getMonth() ', this.maxDate.getMonth());
        LoggerService.log('date.getFullYear() ', date.getFullYear());
        LoggerService.log('this.maxDate.getFullYear() ', this.maxDate.getFullYear());
        LoggerService.log('date.getMonth() <= this.maxDate.getMonth() ', date.getMonth() <= this.maxDate.getMonth());
        LoggerService.log('date.getFullYear() <= this.maxDate.getFullYear() ', date.getFullYear() <= this.maxDate.getFullYear());
        if (date.getFullYear() === this.maxDate.getFullYear()) {
          if (date.getMonth() <= this.maxDate.getMonth()) {
            date.setHours(0, 0, 0, 0);
            this.increment();
          } else {
            LoggerService.warn('Nested ELSE Autosearch is blocked because maxDate criteria is not matching');
          }
        } else if (date.getFullYear() < this.maxDate.getFullYear()) {
          date.setHours(0, 0, 0, 0);
          this.increment();
        } else {
          LoggerService.warn('Autosearch is blocked because maxDate criteria is not matching');
          this.isCalendarLoading = false;
        }
      }
    }
  }

  onSelectDay(day: WeekDay, isUserInteracted = true): void {
    this.selectedMonthViewDay = day;
    this.selectedDate = day.date;
    const selectedDateTime = this.selectedMonthViewDay.date.getTime();
    const dateIndex = this.selectedDays.findIndex(
      selectedDay => selectedDay.date.getTime() === selectedDateTime
    );
    if (dateIndex > -1) {
      delete this.selectedMonthViewDay.cssClass;
      this.selectedDays.splice(dateIndex, 1);
    } else {
      if (this.isMultiselect) {
        this.selectedDays.push(this.selectedMonthViewDay);
      } else {
        delete this.selectedMonthViewDay.cssClass;
        for (const day1 of this.selectedDays) {
          delete day1.cssClass;
        }
        this.selectedDays = [this.selectedMonthViewDay];
      }
      day.cssClass = 'cal-selected';
      this.selectedMonthViewDay = day;
    }
    this.updateCalendarState.next();
    this.selectDaysEvent.emit({
      day: this.selectedDays,
      isUserInteracted
    });
  }

  setBadgeCount(day: any, cssClass: string): void {
    if (this.displayBadgeInBookingFeature) {
      if (this.selectedBookingCartItems?.length > 0) {
        const foundedCardItems: AppointmentCartItem[] = this.selectedBookingCartItems.filter((item: AppointmentCartItem) => {
          return moment(item.startTime, this.dateTimeIsoTFormat)
            .format(this.dateIsoFormat) === moment(day.date).format(this.dateIsoFormat);
        });
        if (foundedCardItems?.length > 0) {
          day.cssClass = cssClass;
          day.badgeTotal = foundedCardItems?.length;
        }
      }
    }
  }

  private refreshHeader(): void {
    this.columnHeaders = this.utils.getWeekViewHeader({
      viewDate: this.viewDate,
      weekStartsOn: 1,
    });
  }
}
